import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  HostBinding,
  Input,
  Output
} from '@angular/core';
import { UmssIconComponent } from '../icon/icon.component';

export interface AvatarData {
  src?: string;
  alt?: string;
  name?: string;
  initials?: string;
  status?: 'Online' | 'Offline' | 'Away' | 'Busy';
}

@Component({
  selector: 'umss-avatar',
  standalone: true,
  imports: [CommonModule, UmssIconComponent],
  templateUrl: './avatar.component.html',
  styleUrls: ['./avatar.component.scss']
})
export class UmssAvatarComponent {
  @HostBinding('style.display') display = 'contents';

  /** Content props */
  @Input() avatarData?: AvatarData;
  @Input() src?: string | null;
  @Input() alt?: string;
  @Input() name?: string;
  @Input() initials?: string;

  /** Variant props */
  @Input() size: 'XSmall' | 'Small' | 'Regular' | 'Large' | 'XLarge' =
    'Regular';
  @Input() shape: 'Circle' | 'Square' | 'Rounded' = 'Circle';
  @Input() variant: 'Image' | 'Initials' | 'Icon' = 'Image';

  /** State props */
  @Input() status?: 'Online' | 'Offline' | 'Away' | 'Busy';
  @Input() disabled: boolean = false;
  @Input() clickable: boolean = false;

  /** Event outputs */
  @Output() avatarClick = new EventEmitter<void>();
  @Output() avatarHover = new EventEmitter<void>();

  /** Computed properties */
  get displaySrc(): string | null | undefined {
    return this.avatarData?.src || this.src;
  }

  get displayAlt(): string {
    return this.avatarData?.alt || this.alt || this.displayName || 'Avatar';
  }

  get displayName(): string {
    return this.avatarData?.name || this.name || '';
  }

  get displayInitials(): string {
    if (this.avatarData?.initials || this.initials) {
      return this.avatarData?.initials || this.initials || '';
    }

    // Generate initials from name
    if (this.displayName) {
      return this.displayName
        .split(' ')
        .map((word) => word.charAt(0))
        .join('')
        .substring(0, 2)
        .toUpperCase();
    }

    return '';
  }

  get displayStatus(): 'Online' | 'Offline' | 'Away' | 'Busy' | undefined {
    return this.avatarData?.status || this.status;
  }

  get hasImage(): boolean {
    return !!(this.displaySrc && this.variant === 'Image');
  }

  get showInitials(): boolean {
    return (
      this.variant === 'Initials' || (!this.hasImage && !!this.displayInitials)
    );
  }

  get showIcon(): boolean {
    return this.variant === 'Icon' || (!this.hasImage && !this.displayInitials);
  }

  get iconSize(): string {
    switch (this.size) {
      case 'XSmall':
        return '12px';
      case 'Small':
        return '16px';
      case 'Regular':
        return '20px';
      case 'Large':
        return '24px';
      case 'XLarge':
        return '32px';
      default:
        return '20px';
    }
  }

  /** Event handlers */
  onAvatarClick(): void {
    if (!this.disabled && this.clickable) this.avatarClick.emit();
  }

  onAvatarHover(): void {
    if (!this.disabled) this.avatarHover.emit();
  }

  onImageError(): void {
    // Fallback to initials or icon when image fails to load
    if (this.variant === 'Image') {
      // Component will automatically show initials or icon based on computed properties
    }
  }
}
